<template>
  <!-- Modal overlay -->
  <div class="modal-overlay">
    <div class="modal">

      <!-- Header -->
      <div class="modal-header">
        <div class="header-left">
          <h2>{{ $t('bo.emailTemplates.editor.title') }}</h2>
          <span class="template-label">{{ templateLabel }}</span>
          <span v-if="isCustomized" class="badge-customised">● {{ $t('bo.emailTemplates.customised') }}</span>
        </div>
        <button class="btn-close" @click="$emit('close')">✕</button>
      </div>

      <!-- Language tabs -->
      <div class="lang-tabs">
        <button
          v-for="lang in SUPPORTED_LANGUAGES"
          :key="lang"
          class="lang-tab"
          :class="{ active: currentLang === lang, customised: isLangCustomized(lang) }"
          @click="switchLang(lang)"
        >
          <span>{{ flagFor(lang) }}</span>
          <span>{{ langLabel(lang) }}</span>
          <span v-if="isLangCustomized(lang)" class="dot-custom"></span>
        </button>
        <label class="auto-translate-toggle">
          <input type="checkbox" v-model="autoTranslate" />
          <span>{{ $t('bo.emailTemplates.editor.autoTranslate') }}</span>
        </label>
      </div>

      <!-- Content area -->
      <div class="modal-body" v-if="!loadingDetails">

        <!-- Subject -->
        <div class="field">
          <label class="field-label">
            {{ $t('bo.emailTemplates.editor.subject') }}
            <span class="char-count">{{ (form.subject || '').length }}/200</span>
          </label>
          <input
            v-model="form.subject"
            type="text"
            class="input-subject"
            maxlength="200"
            :placeholder="$t('bo.emailTemplates.editor.subjectPlaceholder')"
          />
        </div>

        <!-- Body WYSIWYG -->
        <div class="field">
          <label class="field-label">{{ $t('bo.emailTemplates.editor.body') }}</label>

          <TiptapEditor
            ref="tiptapRef"
            :model-value="form.body_html"
            @update:model-value="onBodyUpdate"
            :placeholder="$t('bo.emailTemplates.editor.bodyPlaceholder')"
          >
            <template #variables>
              <div class="vars-panel">
                <div class="vars-panel-title">{{ $t('bo.emailTemplates.variables') }}</div>
                <div
                  v-for="v in allowedVariables"
                  :key="v"
                  class="var-item"
                  @click="insertVariable(v)"
                >
                  &#123;&#123;{{ v }}&#125;&#125;
                </div>
                <div class="vars-insert-hint">{{ $t('bo.emailTemplates.editor.clickToInsert') }}</div>
              </div>
            </template>
          </TiptapEditor>

          <p class="hint-html">{{ $t('bo.emailTemplates.editor.hintHtml') }}</p>
        </div>

        <!-- Important note -->
        <div class="important-note">
          ℹ️ <strong>{{ $t('bo.emailTemplates.editor.importantLabel') }}</strong> {{ $t('bo.emailTemplates.editor.importantText') }}
        </div>

        <!-- Errors -->
        <div v-if="saveError" class="save-error">{{ saveError }}</div>

        <!-- Preview modal (inline) -->
        <div v-if="previewHtml" class="preview-block">
          <div class="preview-header">
            <strong>{{ $t('bo.emailTemplates.editor.previewTitle', { lang: langLabel(currentLang) }) }}</strong>
            <button @click="previewHtml = null" class="btn-close-preview">✕</button>
          </div>
          <div class="preview-subject">{{ $t('bo.emailTemplates.editor.previewSubject', { subject: previewSubject }) }}</div>
          <div class="preview-body" v-html="previewHtml"></div>
        </div>

      </div>

      <div v-else class="loading-details">{{ $t('bo.common.loading') }}</div>

      <!-- Footer actions -->
      <div class="modal-footer">
        <button class="btn-test" @click="sendTest" :disabled="sendingTest">
          {{ sendingTest ? $t('bo.emailTemplates.editor.sending') : $t('bo.emailTemplates.editor.sendTest') }}
        </button>
        <button class="btn-preview" @click="doPreview" :disabled="previewing">
          {{ previewing ? $t('bo.common.loading') : $t('bo.emailTemplates.editor.preview') }}
        </button>
        <div class="footer-right">
          <button
            v-if="isCustomized"
            class="btn-reset"
            @click="doReset"
            :disabled="saving"
          >
            {{ $t('bo.emailTemplates.editor.resetToDefault') }}
          </button>
          <button class="btn-save" @click="doSave" :disabled="saving || !isDirty">
            {{ saving ? $t('bo.common.saving') : $t('bo.emailTemplates.editor.saveTemplate') }}
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import emailTemplateService from '@/services/emailTemplateService'

const { t } = useI18n()
import TiptapEditor from '@/components/ui/TiptapEditor.vue'

const props = defineProps({
  templateKey:      { type: String, required: true },
  language:         { type: String, required: true },
  allowedVariables: { type: Array, default: () => [] },
  // Libelle calcule par la liste (traduction BO, sinon libelle renvoye par l'API).
  label:            { type: String, default: '' },
})

const emit = defineEmits(['close', 'saved'])

// ── Constants ──────────────────────────────────────────────────────────────────
const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'de']
const LANG_LABELS = { en: 'English', fr: 'Français', es: 'Español', de: 'Deutsch' }
const LANG_FLAGS  = { en: '🇬🇧', fr: '🇫🇷', es: '🇪🇸', de: '🇩🇪' }
const TEMPLATE_LABELS = {
  INVITATION_USER:          'User invitation',
  INVITATION_ADMIN_TEAM:    'Admin invitation',
  INVITATION_START_JOURNEY: 'Start journey',
}

const langLabel = (l) => LANG_LABELS[l] ?? l
const flagFor   = (l) => LANG_FLAGS[l] ?? '🌐'

// ── State ─────────────────────────────────────────────────────────────────────
const currentLang     = ref(props.language)
const loadingDetails  = ref(false)
const saving          = ref(false)
const saveError       = ref('')
const previewing      = ref(false)
const sendingTest     = ref(false)
const previewHtml     = ref(null)
const previewSubject  = ref('')
const autoTranslate   = ref(false)
const tiptapRef       = ref(null)

// Per-language form state & original state (for dirty tracking)
const langData = ref({})   // { en: { subject, body_html, is_customized }, fr: {...}, ... }

const form = computed(() => langData.value[currentLang.value] ?? { subject: '', body_html: '' })

const isCustomized       = computed(() => form.value.is_customized ?? false)
const templateLabel      = computed(() => TEMPLATE_LABELS[props.templateKey] ?? (props.label || props.templateKey))
const isLangCustomized   = (lang) => langData.value[lang]?.is_customized ?? false

// Dirty: true if current lang differs from saved state
const isDirty = computed(() => {
  const d = langData.value[currentLang.value]
  if (!d) return false
  return d.subject !== d._saved_subject || d.body_html !== d._saved_body_html
})


// ── Load template details ──────────────────────────────────────────────────────
const loadDetails = async (lang) => {
  if (langData.value[lang]) return  // already loaded

  loadingDetails.value = true
  try {
    const res = await emailTemplateService.get(props.templateKey, lang)

    const effective = res.system_template ?? res.hardcoded_template ?? {}
    langData.value[lang] = {
      subject:        effective.subject ?? '',
      body_html:      effective.body_html ?? '',
      is_customized:  !!res.system_template,
      // Saved snapshots for dirty detection
      _saved_subject:   effective.subject ?? '',
      _saved_body_html: effective.body_html ?? '',
    }
  } catch {
    langData.value[lang] = { subject: '', body_html: '', is_customized: false, _saved_subject: '', _saved_body_html: '' }
  } finally {
    loadingDetails.value = false
  }
}

// ── Tiptap sync ──────────────────────────────────────────────────────────────
const onBodyUpdate = (html) => {
  if (!langData.value[currentLang.value]) return
  langData.value[currentLang.value].body_html = html
}

// ── Variable insertion ────────────────────────────────────────────────────────
const insertVariable = (varName) => {
  tiptapRef.value?.insertText(`{{${varName}}}`)
}

// ── Language switching ────────────────────────────────────────────────────────
const switchLang = async (lang) => {
  const sourceLang = currentLang.value
  currentLang.value = lang
  await loadDetails(lang)

  // Auto-translate: if target lang has no custom template and checkbox is on
  if (autoTranslate.value && !isLangCustomized(lang)) {
    const source = langData.value[sourceLang]
    const target = langData.value[lang]
    if (!target || (!target.subject && !target.body_html)) return

    try {
      const [translatedSubject, translatedBody] = await Promise.all([
        source?.subject   ? emailTemplateService.translate(source.subject,   sourceLang, lang) : null,
        source?.body_html ? emailTemplateService.translate(source.body_html,  sourceLang, lang) : null,
      ])
      if (translatedSubject) langData.value[lang].subject   = translatedSubject
      if (translatedBody)    langData.value[lang].body_html = translatedBody
    } catch {
      // silently ignore — translation is best-effort
    }
  }
}

// ── Save ──────────────────────────────────────────────────────────────────────
const doSave = async () => {
  saving.value   = true
  saveError.value = ''
  const d = langData.value[currentLang.value]

  try {
    await emailTemplateService.save(props.templateKey, {
      language:  currentLang.value,
      subject:   d.subject,
      body_html: d.body_html,
    })

    // Mark as saved & customized
    d._saved_subject   = d.subject
    d._saved_body_html = d.body_html
    d.is_customized    = true

    emit('saved')
  } catch (err) {
    const errors = err.response?.data?.errors
    if (errors) {
      saveError.value = Object.values(errors).flat().join(' ')
    } else {
      saveError.value = err.response?.data?.error ?? t('bo.emailTemplates.editor.failedSave')
    }
  } finally {
    saving.value = false
  }
}

// ── Reset ─────────────────────────────────────────────────────────────────────
const doReset = async () => {
  if (!confirm(t('bo.emailTemplates.editor.confirmReset', { lang: currentLang.value }))) return
  saving.value = true
  try {
    await emailTemplateService.reset(props.templateKey, currentLang.value)
    // Reload this lang
    delete langData.value[currentLang.value]
    await loadDetails(currentLang.value)
  } catch (err) {
    saveError.value = err.response?.data?.error ?? t('bo.emailTemplates.editor.failedReset')
  } finally {
    saving.value = false
  }
}

// ── Preview ───────────────────────────────────────────────────────────────────
const doPreview = async () => {
  previewing.value = true
  previewHtml.value = null
  const d = langData.value[currentLang.value]

  try {
    const res = await emailTemplateService.preview(props.templateKey, {
      language:  currentLang.value,
      subject:   d?.subject,
      body_html: d?.body_html,
    })
    previewSubject.value = res.rendered?.subject ?? ''
    previewHtml.value    = res.rendered?.body_html ?? ''
  } catch {
    saveError.value = t('bo.emailTemplates.editor.failedPreview')
  } finally {
    previewing.value = false
  }
}

// ── Send test ─────────────────────────────────────────────────────────────────
const sendTest = async () => {
  const email = window.prompt(t('bo.emailTemplates.editor.sendTestPrompt'), '')
  if (!email) return

  sendingTest.value = true
  const d = langData.value[currentLang.value]

  try {
    const res = await emailTemplateService.sendTest(props.templateKey, {
      email,
      language:  currentLang.value,
      subject:   d?.subject,
      body_html: d?.body_html,
    })
    alert(res.message)
  } catch (err) {
    alert(err.response?.data?.error ?? t('bo.emailTemplates.editor.failedSendTest'))
  } finally {
    sendingTest.value = false
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  await loadDetails(currentLang.value)
})
</script>

<style scoped>
/* ── Overlay & modal ─────────────────────────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 860px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* ── Header ─────────────────────────────────────────────────────────────────── */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.template-label {
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
}

.badge-customised {
  padding: 0.15rem 0.5rem;
  background: #d1fae5;
  color: #065f46;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 600;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}
.btn-close:hover { background: #f3f4f6; }

/* ── Language tabs ───────────────────────────────────────────────────────────── */
.lang-tabs {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.lang-tab {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid transparent;
  border-radius: 9999px;
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7280;
  position: relative;
  transition: all 0.15s;
}

.lang-tab:hover { background: #e5e7eb; color: #374151; }

.lang-tab.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.lang-tab.customised:not(.active) {
  border-color: #10b981;
  color: #065f46;
}

.dot-custom {
  width: 6px;
  height: 6px;
  background: #10b981;
  border-radius: 50%;
}

.lang-tab.active .dot-custom { background: white; }

.auto-translate-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #6b7280;
  cursor: pointer;
  margin-left: auto;
}

.auto-translate-toggle input { cursor: pointer; }

/* ── Body ────────────────────────────────────────────────────────────────────── */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.loading-details {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

/* ── Fields ──────────────────────────────────────────────────────────────────── */
.field { display: flex; flex-direction: column; gap: 0.4rem; }

.field-label {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
}

.char-count { font-weight: 400; color: #9ca3af; }

.input-subject {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  box-sizing: border-box;
}

.input-subject:focus { outline: none; border-color: #3b82f6; }


/* ── Variables panel ─────────────────────────────────────────────────────────── */
.vars-panel {
  width: 170px;
  min-width: 170px;
  border-left: 1px solid #e5e7eb;
  padding: 0.75rem;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vars-panel-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  margin-bottom: 0.25rem;
}

.var-item {
  padding: 0.3rem 0.5rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  font-size: 0.72rem;
  font-family: monospace;
  color: #059669;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.1s;
}

.var-item:hover {
  background: #ecfdf5;
  border-color: #6ee7b7;
  color: #065f46;
}

.vars-insert-hint {
  margin-top: 0.5rem;
  font-size: 0.68rem;
  color: #3b82f6;
  text-align: center;
  cursor: default;
}

/* ── Hints & notes ───────────────────────────────────────────────────────────── */
.hint-html {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
}

.important-note {
  background: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 0.375rem;
  padding: 0.6rem 0.9rem;
  font-size: 0.8rem;
  color: #92400e;
}

.save-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  padding: 0.6rem 0.9rem;
  font-size: 0.8rem;
  color: #dc2626;
}

/* ── Preview block ───────────────────────────────────────────────────────────── */
.preview-block {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.9rem;
  background: #f3f4f6;
  font-size: 0.8rem;
}

.btn-close-preview {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  color: #6b7280;
}

.preview-subject {
  padding: 0.5rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.preview-body {
  padding: 1rem;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #1f2937;
  max-height: 250px;
  overflow-y: auto;
}

/* ── Footer ──────────────────────────────────────────────────────────────────── */
.modal-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
}

.footer-right { margin-left: auto; display: flex; gap: 0.75rem; }

.btn-test {
  padding: 0.45rem 0.9rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.82rem;
  color: #374151;
  transition: background 0.15s;
}
.btn-test:hover:not(:disabled) { background: #f3f4f6; }

.btn-preview {
  padding: 0.45rem 0.9rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.82rem;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  transition: background 0.15s;
}
.btn-preview:hover:not(:disabled) { background: #f3f4f6; }

.btn-reset {
  padding: 0.45rem 0.9rem;
  border: 1px solid #ef4444;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.82rem;
  color: #dc2626;
  transition: background 0.15s;
}
.btn-reset:hover:not(:disabled) { background: #fef2f2; }

.btn-save {
  padding: 0.45rem 1.1rem;
  border: none;
  background: #3b82f6;
  color: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  transition: background 0.15s;
}
.btn-save:hover:not(:disabled) { background: #2563eb; }

button:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
